import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip, Divider, Stack, Paper } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageIcon from '@mui/icons-material/Image';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import HighlightIcon from '@mui/icons-material/Highlight';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import TitleIcon from '@mui/icons-material/Title';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  uploadImage?: (file: File) => Promise<string | null>;
}

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  tooltip: string;
  onClick: () => void;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ active, disabled, tooltip, onClick, children }) => (
  <Tooltip title={tooltip} arrow>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={(theme) => ({
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          background: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
          borderRadius: 1,
          '&:hover': {
            background: alpha(theme.palette.primary.main, 0.08),
          },
        })}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'محتوای پاپ‌آپ را اینجا بنویسید…',
  minHeight = 220,
  uploadImage,
}) => {
  const theme = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ inline: false, allowBase64: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // sync external value changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleAddLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('آدرس لینک', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleAddImage = useCallback(async () => {
    if (!editor) return;
    if (uploadImage) {
      // file picker
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        const url = await uploadImage(file);
        if (url) {
          editor.chain().focus().setImage({ src: url, alt: file.name }).run();
        }
      };
      input.click();
    } else {
      const url = window.prompt('آدرس تصویر (URL)', 'https://');
      if (url) editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor, uploadImage]);

  if (!editor) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '& .ProseMirror': {
          outline: 'none',
          minHeight,
          padding: 2,
          direction: 'rtl',
          textAlign: 'right',
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'right',
            color: theme.palette.text.disabled,
            pointerEvents: 'none',
            height: 0,
          },
          '& h1, & h2, & h3': { fontWeight: 700, marginTop: '0.8em' },
          '& h1': { fontSize: '1.6rem' },
          '& h2': { fontSize: '1.35rem' },
          '& h3': { fontSize: '1.15rem' },
          '& p': { margin: '0.5em 0', lineHeight: 1.85 },
          '& ul, & ol': { paddingInlineStart: '1.5em' },
          '& blockquote': {
            borderInlineStart: `3px solid ${theme.palette.divider}`,
            paddingInlineStart: 12,
            color: theme.palette.text.secondary,
            margin: '0.6em 0',
          },
          '& a': { color: theme.palette.primary.main, textDecoration: 'underline' },
          '& img': { maxWidth: '100%', borderRadius: 8 },
          '& code': {
            background: alpha(theme.palette.primary.main, 0.08),
            padding: '2px 6px',
            borderRadius: 4,
            fontFamily: 'monospace',
          },
          '& pre': {
            background: alpha(theme.palette.text.primary, 0.06),
            padding: 12,
            borderRadius: 8,
            overflow: 'auto',
            direction: 'ltr',
            textAlign: 'left',
            fontFamily: 'monospace',
          },
          '& mark': { padding: '0 4px', borderRadius: 4 },
          '& hr': {
            border: 'none',
            borderTop: `1px dashed ${theme.palette.divider}`,
            margin: '1em 0',
          },
        },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: alpha(theme.palette.background.default, 0.85),
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 0.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.25} flexWrap="wrap" rowGap={0.5}>
          <ToolbarButton tooltip="بازگرداندن (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
            <UndoIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="انجام مجدد (Ctrl+Shift+Z)" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
            <RedoIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton tooltip="عنوان ۱" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
            <Box sx={{ fontWeight: 700, fontSize: 12 }}>H1</Box>
          </ToolbarButton>
          <ToolbarButton tooltip="عنوان ۲" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
            <Box sx={{ fontWeight: 700, fontSize: 12 }}>H2</Box>
          </ToolbarButton>
          <ToolbarButton tooltip="عنوان ۳" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
            <Box sx={{ fontWeight: 700, fontSize: 12 }}>H3</Box>
          </ToolbarButton>
          <ToolbarButton tooltip="پاراگراف" active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()}>
            <TitleIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton tooltip="پررنگ (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
            <FormatBoldIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="کج (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <FormatItalicIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="زیرخط‌دار (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <FormatUnderlinedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="خط خورده" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <StrikethroughSIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="هایلایت" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight({ color: '#fff59d' } as any).run()}>
            <HighlightIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="کد inline" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
            <CodeIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton tooltip="لیست نقطه‌ای" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <FormatListBulletedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="لیست شماره‌دار" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <FormatListNumberedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="نقل‌قول" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <FormatQuoteIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="بلوک کد" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
            <CodeIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="خط افقی" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <HorizontalRuleIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton tooltip="چپ‌چین" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="وسط‌چین" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="راست‌چین" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
            <FormatAlignRightIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="هم‌چین" active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
            <FormatAlignJustifyIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton tooltip="افزودن لینک" active={editor.isActive('link')} onClick={handleAddLink}>
            <InsertLinkIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="حذف لینک" disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}>
            <LinkOffIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton tooltip="افزودن تصویر" onClick={handleAddImage}>
            <ImageIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton
            tooltip="پاک کردن قالب"
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          >
            <FormatClearIcon fontSize="small" />
          </ToolbarButton>
        </Stack>
      </Box>

      <EditorContent editor={editor} />
    </Paper>
  );
};

export default RichTextEditor;
